import { reportLevelComplete } from 'a/actions/searchlightActions';
import { SEASON, RETRY_ATTEMPTS, RETRY_TIMEOUT_SHORT } from 'a/hub/data/constants';
import { persistStateTypes } from 'a/reducers/persistStateReducer';
import { aquaAxiosWithCreds, isGameIncompleteAndDifferentProduct } from 'root/lib/utilityFunctions';
import {
  postStateError,
  saveGameState,
  responseErrorHandler,
} from 'a/actions/persistStateActions';
import { reportProfileRequest } from 'a/hub/actions/metagameSearchlight';
import { RECOMMENDED_SKILLS_URL, CONTENT_SERVICE_URL } from './endpoints';
import gameIds from '../hub/data/GameIds';
import { getApiErrorMessage, handleAuthFailure } from './apiErrors';
import {
  updateQuestStepsFromProductCache,
  setCachedProductQuestSteps,
  updateVocabStep,
  saveSideQuestData,
} from '../hub/actions/questsActions';
import adjustQuestsContent from '../hub/reducers/adaptivity/contentServiceAdaptivity';

const setGameInQuestForSkill = (contentSets, skillLevels) => (dispatch, getState) => {
  const state = getState();
  const { gamesData, quests, currentActiveQuestId, season } = state.questWorkflowReducer;
  const { product } = state.appSessionReducer;

  dispatch(updatePersistStateReducerWithAdaptivityGames(contentSets));
  if (season === SEASON.GRADE_4_5) {
    dispatch({
      type: persistStateTypes.LOAD_WORKSHOP_GAMES_FROM_ADAPTIVITY,
      gameId: gameIds.METAGAME,
      contentSets,
    });
  } else {
    const questContent = [];
    contentSets.forEach((content) => {
      questContent.push({ ...content.Content[0], skillId: content.Skill });
    });

    questContent.forEach((step) => {
      if (step.GameId.includes('ereader' || 'eReader')) {
        if (gamesData) {
          const storedEreaderBookData = gamesData[step.GameId];
          if (storedEreaderBookData && storedEreaderBookData.GameData) {
            const { gameDetails } = storedEreaderBookData.GameData;
            if (gameDetails) {
              storedEreaderBookData.GameData.gameDetails.status = '';
              storedEreaderBookData.GameData.gameDetails.attempts = 0;
              dispatch(saveGameState(step.GameId, { ...storedEreaderBookData.GameData }));
              let currentLevel = 0;
              if (gameDetails.levels) {
                [currentLevel = 0] = gameDetails.levels[0];
              }
              const questId = season === SEASON.GRADE_K_1
                ? currentActiveQuestId
                : currentActiveQuestId - 200;
              // Check for chapter book
              if (currentLevel !== 0) {
                for (let i = 1; i < questId; ++i) {
                  const { steps } = quests[`quest${season === SEASON.GRADE_K_1 ? i : i + 200}`];
                  steps.forEach(({ type, game, chapter }) => {
                    if (type === 'ereader' && game === step.GameId && chapter === step.Chapter) {
                      dispatch(reportLevelComplete(step.GameId, {
                        level: currentLevel + 1,
                        isSuccess: true,
                        isQuestWorkflow: true,
                      }));
                    }
                  });
                }
              } else {
                dispatch(reportLevelComplete(step.GameId, { level: 1, isSuccess: true, isQuestWorkflow: true }));
              }
            }
          }
        }
      }
    });

    dispatch(loadAdaptivityMap(contentSets, product, skillLevels));
  }
};

const loadAdaptivityMap = (contentSets, product, skillLevels) => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  const { adaptivityVersion } = questWorkflowReducer;

  const [newQuestState, adaptivityQuestSteps] = adaptivityVersion === 3
    ? adjustQuestsContent(questWorkflowReducer, contentSets, product, skillLevels)
    : [questWorkflowReducer, null];

  dispatch({
    type: persistStateTypes.LOAD_ADAPTIVITY_MAP,
    gameId: gameIds.METAGAME,
    payload: {
      newQuestState,
    },
  });
  dispatch(saveSideQuestData());
  if (adaptivityQuestSteps) {
    dispatch(setCachedProductQuestSteps(adaptivityQuestSteps, product));
  }
  dispatch(updateVocabStep());
};

function checkRetry(retryAttempt, message, action, dispatch) {
  if (retryAttempt < RETRY_ATTEMPTS) {
    console.log(`${message} Attempt ${retryAttempt + 1}`);
    // schedule next retry in 1 second
    setTimeout(() => dispatch(action), RETRY_TIMEOUT_SHORT);
  }
}
function responseHandler(dispatch, error, api) {
  dispatch(postStateError(gameIds.METAGAME, error));
  dispatch(reportProfileRequest([], 'false', { error: getApiErrorMessage(error), API: api }));
}

/*
* Function for getting Adaptivity Map from server
* we call it on Map and each time we enter quest to get updated content if any
*/
export const getAdaptivityMap = (retryAttempt = 0) => async (dispatch, getState) => {
  const state = getState();
  const { product } = state.appSessionReducer;
  const {
    adaptivityVersion,
    contentServiceQuestId,
    currentActiveQuestId,
    quests,
    season,
  } = state.questWorkflowReducer;
  const questStepsCache = state.questWorkflowReducer[`${product}QuestStepsCache`];
  const {
    currentQuestId,
    workshop,
  } = state.workshopReducer;
  if (adaptivityVersion === 0) {
    return dispatch({ type: 'NONE', message: 'adaptivity version is 0' });
  }
  const isWorkshop = () => season === SEASON.GRADE_4_5;
  const questId = isWorkshop() ? currentQuestId : currentActiveQuestId;
  if (questId === 0) {
    // all quests are finished
    return dispatch({ type: 'NONE', message: 'all quests are finished' });
  }
  const games = isWorkshop() ? workshop[`quest${questId}`].games : quests[`quest${questId}`].steps;
  const shouldSwapGames = games.some(
    game => (!isWorkshop() && isGameIncompleteAndDifferentProduct(game, product)),
  );
  const hasHardcodedGames = games.some(game => game.source === 'hard-coded');
  if (!isWorkshop()
    && contentServiceQuestId === questId
    && !shouldSwapGames
    && !hasHardcodedGames
  ) {
    // current quest is locked
    return dispatch({ type: 'NONE', message: 'locked quest via contentServiceQuestId' });
  }
  if (isWorkshop() && games.filter(game => game.levelsMastered.length > 0 && game.source === 'adaptivity').length) {
    // current quest content is locked when some progress is done in a quest step from adaptivity
    return dispatch({ type: 'NONE', message: 'locked quest via progress made in adaptivity' });
  }
  // keep current quest if it has no incomplete steps of a different product
  if (!shouldSwapGames && !hasHardcodedGames) {
    return dispatch({ type: 'NONE', message: 'locked quest when product did not change and has adaptivity quests' });
  }
  if ((shouldSwapGames || hasHardcodedGames) && questStepsCache.length) {
    return dispatch(updateQuestStepsFromProductCache(questStepsCache, product));
  }
  dispatch(requestRecommendedSkills());
  const { persistStateReducer: { skillMap, studentId } } = state;
  try {
    const { data } = await aquaAxiosWithCreds(
      `${RECOMMENDED_SKILLS_URL}/profile?userId=${studentId}&skillMap=${skillMap}`,
    );
    const userSkills = data.Data.RecommendedSkills;
    const skillLevels = data.Data.SkillLevels.filter(skillLevel => skillLevel.Source === 'level_result');
    // skill service + content-service
    const skillsMap = userSkills.map(skill => skill.Skill).filter(Boolean);
    if (skillsMap.length) {
      dispatch(getContentSet(skillsMap.join(','), data.Meta, skillLevels));
    } else {
      // no recommended skills, save hard-coded games to cache so progress is saved upon switching products
      dispatch(setCachedProductQuestSteps(games, product));
    }
  } catch (error) {
    responseHandler(
      dispatch,
      error,
      `${RECOMMENDED_SKILLS_URL}/profile?userId=${studentId}&skillMap=${skillMap}  Attempt - ${retryAttempt + 1}`,
    );
    if (!handleAuthFailure(error)) {
      checkRetry(retryAttempt, 'Retry getting Adaptivity data.', getAdaptivityMap(retryAttempt + 1), dispatch);
    }
  }
  return 0;
};

/*
* Function for getting Content Set based on Skill
*/
export const getContentSet = (skill, metadata, skillLevels, retryAttempt = 0) => async (dispatch, getState) => {
  dispatch(requestContentSet());
  const { persistStateReducer: { skillMap } } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(
      `${CONTENT_SERVICE_URL}/gameContentSets?skills=${skill}&skillMap=${skillMap}`,
    );
    dispatch(setGameInQuestForSkill(data.GameContentSets, skillLevels));
    dispatch(reportProfileRequest(data.GameContentSets, 'true', metadata));
  } catch (error) {
    responseHandler(
      dispatch,
      error,
      `${CONTENT_SERVICE_URL}/gameContentSets?skills=${skill}&skillMap=${skillMap}  Attempt - ${retryAttempt + 1}`,
    );
    if (!handleAuthFailure(error)) {
      checkRetry(retryAttempt, 'Retry getting Game Content Sets data.',
        getContentSet(skill, metadata, skillLevels, retryAttempt + 1), dispatch);
    }
  }
};

export const getProfile = () => async (dispatch, getState) => {
  // Post started
  dispatch(requestProfile());
  const {
    persistStateReducer: { skillMap, studentId },
    appSessionReducer: { product },
  } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(
      `${RECOMMENDED_SKILLS_URL}/profile?userId=${studentId}&skillMap=${skillMap}`);
    dispatch(setProfile(data, product));
  } catch (error) {
    responseErrorHandler(error, gameIds.METAGAME, dispatch, 'POST');
  }
};

/*
* Function for getting User Skills from Content Service
* requests all skills for Curioso Powers Page
*/
export const getUserSkills = () => async (dispatch, getState) => {
  dispatch(requestContentSet());
  const { persistStateReducer: { skillMap } } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(`${CONTENT_SERVICE_URL}/gameContentSets?skillMap=${skillMap}`);
    dispatch(updateUserSkills(data.GameContentSets));
    dispatch(getProfile());
  } catch (error) {
    responseErrorHandler(error, gameIds.METAGAME, dispatch, 'POST');
  }
};

/*
  We can get current recommended games and use them in Admin when we want to update current quest with games from skill map
*/
export const getAdaptivityContentForAdminTool = () => async (dispatch, getState) => {
  const {
    persistStateReducer: { skillMap, studentId },
  } = getState();
  try {
    const { data } = await aquaAxiosWithCreds(
      `${RECOMMENDED_SKILLS_URL}/profile?userId=${studentId}&skillMap=${skillMap}`);
    const userSkills = data.Data.RecommendedSkills;
    // skill service + content-service
    const skillsMap = userSkills.map(skill => skill.Skill);
    const { data: content } = await aquaAxiosWithCreds(
      `${CONTENT_SERVICE_URL}/gameContentSets?skills=${skillsMap.join(',')}&skillMap=${skillMap}`);
    dispatch(updatePersistStateReducerWithAdaptivityGames(content.GameContentSets));
  } catch (error) {
    console.log('An error occured during Profile post', error);
  }
};

/**
 * Redux Action - Recommended skills are being requested
 */

function requestRecommendedSkills() {
  return {
    type: persistStateTypes.REQUEST_RECOMMENDED_SKILLS,
    gameId: gameIds.METAGAME,
  };
}

/**
 * Redux Action - Content Set is being requested
 */

function requestContentSet() {
  return {
    type: persistStateTypes.REQUEST_CONTENT_SET,
    gameId: gameIds.METAGAME,
  };
}

function updatePersistStateReducerWithAdaptivityGames(contentSets) {
  return (dispatch) => {
    dispatch({
      type: persistStateTypes.SAVE_ADAPTIVITY_CONTENT,
      gameId: gameIds.METAGAME,
      contentSets,
    });
  };
}

function updateUserSkills(skillsSet) {
  return {
    type: persistStateTypes.LOAD_USER_SKILLS,
    gameId: gameIds.METAGAME,
    skillsSet,
  };
}

function requestProfile() {
  return {
    type: persistStateTypes.REQUEST_PROFILE,
    gameId: gameIds.METAGAME,
  };
}

function setProfile(json, product) {
  return {
    type: persistStateTypes.SET_PROFILE,
    gameId: gameIds.METAGAME,
    json,
    product,
  };
}
